/**
 * Canonical CSS color parsing and formatting for parent-side editing UI.
 * Browser CSS parsing handles named/HSL/modern forms; css-tree provides a
 * deterministic fallback for hex, rgb(), color(srgb), and var() values.
 */

import * as csstree from '../vendor/csstree.esm.js';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function firstValueNode(value) {
  try {
    const ast = csstree.parse(String(value || '').trim(), {
      context: 'value',
      parseCustomProperty: true
    });
    const children = [...(ast.children || [])];
    return children.length === 1 ? children[0] : null;
  } catch {
    return null;
  }
}

function parseHexNode(node) {
  if (node?.type !== 'Hash') return null;
  const hex = String(node.value || '');
  if (![3, 4, 6, 8].includes(hex.length) || !/^[0-9a-f]+$/i.test(hex)) return null;
  const expanded = hex.length <= 4 ? [...hex].map((part) => part + part).join('') : hex;
  const channels = [0, 2, 4].map((index) => Number.parseInt(expanded.slice(index, index + 2), 16));
  if (channels.some((channel) => !Number.isFinite(channel))) return null;
  const alphaByte = expanded.length === 8 ? Number.parseInt(expanded.slice(6, 8), 16) : 255;
  return { r: channels[0], g: channels[1], b: channels[2], a: alphaByte / 255 };
}

function numericFunctionParts(node) {
  return [...(node?.children || [])]
    .filter((child) => child.type === 'Number' || child.type === 'Percentage')
    .map((child) => ({
      value: Number.parseFloat(child.value),
      percentage: child.type === 'Percentage'
    }));
}

function parseRgbFunction(node) {
  const name = String(node?.name || '').toLowerCase();
  if (node?.type !== 'Function' || (name !== 'rgb' && name !== 'rgba')) return null;
  const parts = numericFunctionParts(node);
  if (parts.length < 3 || parts.some((part) => !Number.isFinite(part.value))) return null;
  const channel = (part) => clamp(part.percentage ? part.value * 2.55 : part.value, 0, 255);
  const alphaPart = parts[3];
  const alpha = alphaPart
    ? clamp(alphaPart.percentage ? alphaPart.value / 100 : alphaPart.value, 0, 1)
    : 1;
  return { r: channel(parts[0]), g: channel(parts[1]), b: channel(parts[2]), a: alpha };
}

function parseSrgbFunction(node) {
  if (node?.type !== 'Function' || String(node.name || '').toLowerCase() !== 'color') return null;
  const children = [...(node.children || [])];
  const colorSpace = children.find((child) => child.type === 'Identifier');
  if (String(colorSpace?.name || '').toLowerCase() !== 'srgb') return null;
  const parts = numericFunctionParts(node);
  if (parts.length < 3 || parts.some((part) => !Number.isFinite(part.value))) return null;
  const channel = (part) => clamp(part.percentage ? part.value * 2.55 : part.value * 255, 0, 255);
  const alphaPart = parts[3];
  const alpha = alphaPart
    ? clamp(alphaPart.percentage ? alphaPart.value / 100 : alphaPart.value, 0, 1)
    : 1;
  return { r: channel(parts[0]), g: channel(parts[1]), b: channel(parts[2]), a: alpha };
}

function parseDirectColor(value) {
  const node = firstValueNode(value);
  return parseHexNode(node) || parseRgbFunction(node) || parseSrgbFunction(node);
}

/** Parse a top-level var() expression without string-scanning CSS syntax. */
export function parseCssVarReference(value) {
  const node = firstValueNode(value);
  if (node?.type !== 'Function' || String(node.name || '').toLowerCase() !== 'var') return null;
  const children = [...(node.children || [])];
  const tokenNode = children[0];
  if (tokenNode?.type !== 'Identifier' || !String(tokenNode.name || '').startsWith('--'))
    return null;
  const commaIndex = children.findIndex(
    (child) => child.type === 'Operator' && child.value === ','
  );
  const fallback =
    commaIndex >= 0
      ? children
          .slice(commaIndex + 1)
          .map((child) => csstree.generate(child))
          .join('')
          .trim()
      : '';
  return { token: tokenNode.name, fallback };
}

/** Resolve a chain of top-level var() references from an explicit token map. */
export function resolveCssColorValue(value, variables = {}, maxDepth = 8) {
  let current = String(value || '').trim();
  const seen = new Set();
  for (let depth = 0; depth < maxDepth; depth += 1) {
    const ref = parseCssVarReference(current);
    if (!ref) return current;
    if (seen.has(ref.token)) return ref.fallback || '';
    seen.add(ref.token);
    current = String(variables?.[ref.token] || ref.fallback || '').trim();
    if (!current) return '';
  }
  return '';
}

function browserParsedColor(value, { document: doc, contextElement, variables } = {}) {
  if (!doc?.createElement) return null;
  const probe = doc.createElement('span');
  probe.setAttribute('data-pb-ignore', '');
  probe.style.cssText =
    'position:absolute;left:-9999px;top:0;width:0;height:0;visibility:hidden;pointer-events:none;';
  for (const [name, tokenValue] of Object.entries(variables || {})) {
    if (name.startsWith('--') && typeof tokenValue === 'string')
      probe.style.setProperty(name, tokenValue);
  }
  probe.style.color = '';
  probe.style.color = String(value || '').trim();
  if (!probe.style.color) return null;

  const host = contextElement?.appendChild ? contextElement : doc.body || doc.documentElement;
  if (!host?.appendChild) return null;
  host.appendChild(probe);
  try {
    const computed = doc.defaultView?.getComputedStyle?.(probe)?.color || probe.style.color;
    return parseDirectColor(computed);
  } finally {
    probe.remove();
  }
}

function canvasParsedColor(value, doc) {
  const win = doc?.defaultView;
  if (!doc?.createElement || typeof win?.CanvasRenderingContext2D !== 'function') return null;
  try {
    const canvas = doc.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    const raw = String(value || '').trim();
    const acceptedAgainst = (sentinel) => {
      ctx.fillStyle = sentinel;
      ctx.fillStyle = raw;
      return String(ctx.fillStyle || '').toLowerCase();
    };
    const firstSentinel = '#010203';
    const secondSentinel = '#040506';
    const first = acceptedAgainst(firstSentinel);
    const second = acceptedAgainst(secondSentinel);
    // Canvas keeps the prior fillStyle when assignment is invalid. Two
    // sentinels distinguish rejection from a valid color equal to one sentinel.
    if (first === firstSentinel && second === secondSentinel) return null;
    ctx.fillStyle = second;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, alpha] = ctx.getImageData(0, 0, 1, 1).data;
    return { r, g, b, a: alpha / 255 };
  } catch {
    return null;
  }
}

/**
 * Parse any browser-supported CSS color into sRGB channels. currentColor is
 * resolved from the explicit option first, then from contextElement.
 */
export function parseCssColor(value, options = {}) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const doc = options.document || globalThis.document;
  const lower = raw.toLowerCase();
  if (lower === 'currentcolor') {
    const inherited =
      String(options.currentColor || '').trim() ||
      String(doc?.defaultView?.getComputedStyle?.(options.contextElement)?.color || '').trim();
    if (!inherited || inherited.toLowerCase() === 'currentcolor') return null;
    return parseCssColor(inherited, { ...options, currentColor: '' });
  }

  const resolved = resolveCssColorValue(raw, options.variables || {});
  if (!resolved) return null;
  const direct = parseDirectColor(resolved);
  if (direct) return direct;
  const browser = browserParsedColor(resolved, {
    document: doc,
    contextElement: options.contextElement,
    variables: options.variables
  });
  return browser || canvasParsedColor(resolved, doc);
}

/** True when the browser accepts a value for the CSS color property. */
export function isValidCssColor(value, options = {}) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (parseCssColor(raw, options)) return true;
  const doc = options.document || globalThis.document;
  if (!doc?.createElement) return false;
  const probe = doc.createElement('span');
  probe.style.color = '';
  probe.style.color = raw;
  return !!probe.style.color;
}

export function formatHexColor(color, { includeAlpha = 'auto' } = {}) {
  if (!color) return '';
  const byte = (value) =>
    Math.round(clamp(Number(value) || 0, 0, 255))
      .toString(16)
      .padStart(2, '0');
  const alpha = clamp(Number.isFinite(Number(color.a)) ? Number(color.a) : 1, 0, 1);
  const base = `#${byte(color.r)}${byte(color.g)}${byte(color.b)}`;
  const shouldIncludeAlpha = includeAlpha === true || (includeAlpha === 'auto' && alpha < 0.999);
  return shouldIncludeAlpha ? `${base}${byte(alpha * 255)}` : base;
}

export function colorToHex(value, options = {}) {
  const parsed = parseCssColor(value, options);
  return parsed
    ? formatHexColor(parsed, { includeAlpha: options.includeAlpha ?? 'auto' })
    : options.fallback || '';
}

export function colorToPickerHex(value, options = {}) {
  const parsed = parseCssColor(value, options);
  return parsed ? formatHexColor(parsed, { includeAlpha: false }) : options.fallback || '';
}

export function normalizeCssColor(value, options = {}) {
  const parsed = parseCssColor(value, options);
  if (!parsed) return '';
  const r = Math.round(parsed.r);
  const g = Math.round(parsed.g);
  const b = Math.round(parsed.b);
  const alpha = Math.round(parsed.a * 1000) / 1000;
  return alpha < 0.999 ? `rgba(${r}, ${g}, ${b}, ${alpha})` : `rgb(${r}, ${g}, ${b})`;
}

export function colorsEqual(left, right, options = {}) {
  const a = parseCssColor(left, options);
  const b = parseCssColor(right, options);
  if (!a || !b) return false;
  return (
    Math.abs(a.r - b.r) <= 1 &&
    Math.abs(a.g - b.g) <= 1 &&
    Math.abs(a.b - b.b) <= 1 &&
    Math.abs(a.a - b.a) <= 0.01
  );
}

export function isTransparentColor(value, options = {}) {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (!raw || raw === 'transparent') return true;
  const parsed = parseCssColor(value, options);
  return !!parsed && parsed.a <= 0.001;
}
